import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import toast from 'react-hot-toast';
import {
  QuestionnaireDefinitionData,
  PopupButton,
} from '../../types/questionnaire.types';
import WizardRenderField from './WizardRenderField';
import { useQuestionnaireWizard } from '../../hooks/useQuestionnaireWizard';
import { WizardLayout } from './WizardLayout';
import { WizardSectionContent } from './WizardSectionContent';
import { WizardPopupSystem } from './WizardPopupSystem';
import { EmailVerificationPopup } from './EmailVerificationPopup';
import { getApiError } from '../../utils/apiError';
import {
  convertToAnswer,
  getFieldsToValidate,
} from '../../utils/questionnaireFieldUtils';
import { evaluateFieldVisibility, REFERRAL_CONDITION_KEY, LEAD_CONDITION_KEY, LEAD_PRESENT_VALUE } from '../../utils/conditionalLogic';
import { resolvePaymentAmount, resolveDiscountText } from '../../utils/paymentConfig';

const fieldHasMapping = (field: any, mapping: string): boolean => {
  if (field.patientFieldMapping === mapping) return true;
  return (field.nestedFields || []).some((nf: any) => fieldHasMapping(nf, mapping));
};

const findSectionIndexByMapping = (sections: any[], mapping: string): number =>
  sections.findIndex((section: any) =>
    (section.fields || []).some((field: any) => fieldHasMapping(field, mapping))
  );

export interface QuestionnaireWizardBodyProps {
  // Questionnaire data
  definition: QuestionnaireDefinitionData;
  questionnaireId: number | null;
  tenantId: number;

  // Pre-loaded state from wrapper
  initialToken: string | null;
  initialAnswers: Record<string, any>;
  initialSectionIndex: number;
  initialScores: Record<string, any>;
  /** Email-verification status loaded from the server: field name -> {verified, email} */
  initialEmailVerifications?: Record<string, { verified: boolean; email: string }>;

  // Called on first save when token is null (Public mode: create questionnaire)
  // Returns token string. Generic mode: just returns existing token.
  ensureToken: () => Promise<string | null>;
  // Called when token is freshly created (Public: save to localStorage)
  onTokenAcquired?: (token: string) => void;

  // API adapters (all closures that capture their context)
  saveAnswers: (token: string, answers: any[], lastSection?: { title: string; index: number }) => Promise<any>;
  uploadFile: (token: string, file: globalThis.File, fieldName: string) => Promise<{ id: number; file_name: string; file_size: number }>;
  calculateScoreApi: (token: string, scoreName: string) => Promise<any>;
  submitQuestionnaire: (token: string) => Promise<any>;
  /** Send a 6-digit verification code to an email field's value. When omitted, email verification is disabled. */
  sendEmailCode?: (token: string, fieldName: string, email: string) => Promise<any>;
  /** Validate a verification code. Resolves with { verified } */
  verifyEmailCode?: (token: string, fieldName: string, code: string) => Promise<{ verified: boolean }>;

  // Post-action callbacks
  onExitConfirmed: (token: string | null, exitLocation?: string) => Promise<void>;
  onExitNavigate: (exitUrl?: string) => void;
  onComplete: () => void;

  // Config
  exitPopupVariant: 'screening' | 'questionnaire';
  /** When true, the questionnaire was already submitted — open at the section after the submit button */
  isSubmitted?: boolean;
  /** Language code — 'he' enables RTL layout and Hebrew error messages */
  language?: string;
  /** Clinic state — 'IL' or 'UK', used for patient-field-mapped validation */
  clinicState?: string | null;
  /** When true, hides the logo and uses height: 100% for embedded rendering */
  isEmbedded?: boolean;
  /** Custom header logo URL (e.g. clinic logo for il_webhook questionnaires) */
  logoSrc?: string;
  rightLogoSrc?: string;
  /** Referral stored on the questionnaire; drives referral-based conditional rules */
  referralId?: number | null;
  /** Whether the questionnaire is tied to a lead; drives lead-based section/field rules */
  hasLead?: boolean;
  /** Resolved {variable} values for content substitution (lead vars for create_patient, patient vars for collect_info) */
  variableValues?: Record<string, string>;
}

export const QuestionnaireWizardBody: React.FC<QuestionnaireWizardBodyProps> = (props) => {
  const {
    definition,
    questionnaireId,
    // tenantId is part of the props interface but not used directly in the body
    initialToken,
    initialAnswers,
    initialSectionIndex,
    initialScores,
    initialEmailVerifications,
    ensureToken,
    onTokenAcquired,
    saveAnswers,
    uploadFile,
    calculateScoreApi,
    submitQuestionnaire,
    sendEmailCode,
    verifyEmailCode,
    onExitConfirmed,
    onExitNavigate,
    onComplete,
    exitPopupVariant,
    isSubmitted = false,
    language,
    clinicState,
    isEmbedded = false,
    logoSrc,
    rightLogoSrc,
    referralId,
    hasLead = false,
    variableValues,
  } = props;

  const [token, setToken] = useState<string | null>(initialToken);
  const [activeStep, setActiveStep] = useState(0);
  const [allAnswers, setAllAnswers] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [submittedAtIndex, setSubmittedAtIndex] = useState<number | null>(null);

  // Email verification state. emailVerifiedRef maps fieldName -> verified email value;
  // it's a ref so handleNext re-runs see freshly-verified fields synchronously.
  const emailVerifiedRef = useRef<Record<string, string>>({});
  const [verification, setVerification] = useState<{
    open: boolean;
    fieldName: string;
    email: string;
    busy: boolean;
    error: string | null;
    info: string | null;
  }>({ open: false, fieldName: '', email: '', busy: false, error: null, info: null });

  const sections = definition?.sections || [];
  const totalSections = sections.length;
  const exitPopupId = definition?.exitPopupId;

  const wizard = useQuestionnaireWizard({
    sections,
    totalSections,
    sectionIndex: activeStep,
    setSectionIndex: setActiveStep,
    token,
    exitPopupId,
    exitPopupVariant,
    hasLead,
    onAutoAdvanceSection: async (skipNavigation: boolean) => handleNext(skipNavigation),
  });

  // Values used for conditional-logic evaluation: the answers plus the
  // questionnaire's referral, so rules can depend on the referral like any question.
  const {
    control,
    trigger,
    setValue,
    reset,
    getValues,
    formState,
    formValues,
    saving,
    setSaving,
    openPopupId,
    showExitPopup,
    setShowExitPopup,
    paymentFormReady,
    setPaymentFormReady,
    paymentAmount,
    sectionPageIndex,
    setSectionPageIndex,
    scores,
    cameFromBackNav,
    setCameFromBackNav,
    navigateAfterPopup,
    setNavigateAfterPopup,
    setButtonGroupPopupContext,
    navigateToPage,
    setNavigateToPage,
    scrollContainerRef,
    stripeCCFieldRef,
    autoAdvanceTriggeredRef,
    targetPageIndexRef,
    skipBeforeUnloadRef,
    isMobile,
    currentSection,
    updateScores,
    isSectionVisible,
    triggerExitPopup,
    getExitPopupContent,
    handleOpenPopup,
    handleClosePopup,
    scrollToFirstError,
    loadPaymentAmountIfNeeded,
    navigateToPrevVisibleSection,
    getFieldsForCurrentPage,
    hasMorePages,
    injectCustomIcons,
  } = wizard;

  const valuesWithReferral = useMemo(
    () => ({
      ...formValues,
      [REFERRAL_CONDITION_KEY]: referralId ?? '',
      [LEAD_CONDITION_KEY]: hasLead ? LEAD_PRESENT_VALUE : '',
    }),
    [formValues, referralId, hasLead]
  );

  // Displayed amount follows the same per-referral rule the backend charges by
  const configuredPaymentAmount = useMemo(
    () => resolvePaymentAmount(definition?.payment_config, referralId),
    [definition, referralId]
  );

  // Optional per-referral discount copy, shown under the total in the summary box
  const paymentDiscountText = useMemo(
    () => resolveDiscountText(definition?.payment_config, referralId),
    [definition, referralId]
  );

  const { errors } = formState;

  // Mount effect — runs once to restore pre-loaded state
  useEffect(() => {
    reset(initialAnswers);
    setActiveStep(initialSectionIndex);
    setAllAnswers(initialAnswers);
    if (Object.keys(initialScores).length > 0) {
      flushSync(() => updateScores(initialScores));
    }

    // Seed already-verified emails so we don't re-prompt for them on resume
    const verifiedInit: Record<string, string> = {};
    Object.entries(initialEmailVerifications || {}).forEach(([fieldName, status]) => {
      if (status?.verified && status?.email) verifiedInit[fieldName] = status.email;
    });
    emailVerifiedRef.current = verifiedInit;

    // If reopening after submission, navigate to the section after the submit button
    // and block back navigation to pre-submit sections
    const submitSectionIndex = sections.findIndex((s: any) =>
      s.fields?.some((f: any) => f.type === 'button' && f.action === 'submit')
    );
    if (submitSectionIndex >= 0) {
      if (isSubmitted) {
        // Find the next section after submit and start there
        const nextIdx = submitSectionIndex + 1;
        if (nextIdx < sections.length) {
          setSubmittedAtIndex(submitSectionIndex);
          setActiveStep(nextIdx);
        }
      } else if (initialSectionIndex > submitSectionIndex) {
        // Already past submit point from a previous session
        setSubmittedAtIndex(submitSectionIndex);
      }
    }

    // Email-verification resume guard: if the resume point is past a section that has a
    // filled email field requiring verification which is NOT yet verified, clamp back to
    // that section. Otherwise a refresh (answers are saved before verification) would
    // skip the section and bypass the code check. Only relevant when verification is wired.
    if (sendEmailCode && verifyEmailCode && !isSubmitted) {
      const targetIndex = initialSectionIndex;
      for (let i = 0; i < targetIndex && i < sections.length; i++) {
        const section = sections[i];
        const hasUnverifiedEmail = flattenFields(section?.fields || []).some((f: any) => {
          if (f.type !== 'email' || !f.validateEmail) return false;
          if (!evaluateFieldVisibility(f.name, { ...initialAnswers, [REFERRAL_CONDITION_KEY]: referralId ?? '', [LEAD_CONDITION_KEY]: hasLead ? LEAD_PRESENT_VALUE : '' }, undefined, f.conditionalLogic)) return false;
          const value = (initialAnswers[f.name] ?? '').toString().trim();
          if (!value) return false; // empty optional field — nothing to verify
          return verifiedInit[f.name] !== value; // not verified for this value
        });
        if (hasUnverifiedEmail) {
          setActiveStep(i);
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Answer building ───────────────────────────────────────────────────────

  const buildSectionAnswers = useCallback(
    (fields: any[], sectionIndex: number, extraOffset = 0) => {
      // Read values via RHF's path-aware getValues(name) rather than flat formValues[name]:
      // field names containing "." (e.g. "10. Fidgeting") are treated by react-hook-form as
      // nested paths, so flat lookup returns undefined and the answer would be saved empty.
      return fields.flatMap((field: any, fieldIndex: number) => {
        // Skip button and content fields
        if (field.type === 'button' || field.type === 'content') {
          return [];
        }
        // For line and container fields, return answers from nested fields
        if (field.type === 'line' || field.type === 'container') {
          return (field.nestedFields || []).flatMap((nestedField: any, nestedIndex: number) => {
            // Skip button and content nested fields
            if (nestedField.type === 'button' || nestedField.type === 'content') {
              return [];
            }
            // Check if nested field is also a line field (triple nesting: container → line → fields)
            if (nestedField.type === 'line') {
              return (nestedField.nestedFields || [])
                .filter((lf: any) => lf.type !== 'button' && lf.type !== 'content')
                .map((lineField: any, lineIndex: number) => {
                  const value = getValues(lineField.name);
                  return {
                    order: sectionIndex * 100 + extraOffset + fieldIndex * 10 + nestedIndex + lineIndex + 1,
                    question_name: lineField.name,
                    answer: convertToAnswer(value, lineField.type),
                  };
                });
            }
            // Regular nested field
            const value = getValues(nestedField.name);
            return [{
              order: sectionIndex * 100 + extraOffset + fieldIndex * 10 + nestedIndex + 1,
              question_name: nestedField.name,
              answer: convertToAnswer(value, nestedField.type),
            }];
          });
        }
        // For regular fields, return the answer
        const value = getValues(field.name);
        return [{
          order: sectionIndex * 100 + extraOffset + fieldIndex + 1,
          question_name: field.name,
          answer: convertToAnswer(value, field.type),
        }];
      });
    },
    [getValues]
  );

  // ── File upload ───────────────────────────────────────────────────────────

  const uploadPendingFiles = useCallback(
    async (fields: any[], tok: string) => {
      for (const field of fields) {
        if (field.type === 'file') {
          const value = formValues[field.name];
          if (value) {
            const allowMultiple = field.fileConfig?.allowMultiple || false;

            if (allowMultiple && Array.isArray(value)) {
              const needsUpload = value.some((item: any) => item instanceof File);
              if (needsUpload) {
                const uploadedFiles = await Promise.all(
                  value.map(async (item: any) => {
                    if (item instanceof File) {
                      const uploaded = await uploadFile(tok, item, field.name);
                      return {
                        id: uploaded.id,
                        name: uploaded.file_name,
                        size: uploaded.file_size,
                      };
                    }
                    return item; // Already uploaded
                  })
                );
                setValue(field.name, uploadedFiles);
              }
            } else if (value instanceof File) {
              const uploaded = await uploadFile(tok, value, field.name);
              setValue(field.name, {
                id: uploaded.id,
                name: uploaded.file_name,
                size: uploaded.file_size,
              });
            }
          }
        }
        // Also check nested fields in line and container fields
        if ((field.type === 'line' || field.type === 'container') && field.nestedFields) {
          for (const nestedField of field.nestedFields) {
            if (nestedField.type === 'file') {
              const value = formValues[nestedField.name];
              if (value) {
                const allowMultiple = nestedField.fileConfig?.allowMultiple || false;

                if (allowMultiple && Array.isArray(value)) {
                  const needsUpload = value.some((item: any) => item instanceof File);
                  if (needsUpload) {
                    const uploadedFiles = await Promise.all(
                      value.map(async (item: any) => {
                        if (item instanceof File) {
                          const uploaded = await uploadFile(tok, item, nestedField.name);
                          return {
                            id: uploaded.id,
                            name: uploaded.file_name,
                            size: uploaded.file_size,
                          };
                        }
                        return item;
                      })
                    );
                    setValue(nestedField.name, uploadedFiles);
                  }
                } else if (value instanceof File) {
                  const uploaded = await uploadFile(tok, value, nestedField.name);
                  setValue(nestedField.name, {
                    id: uploaded.id,
                    name: uploaded.file_name,
                    size: uploaded.file_size,
                  });
                }
              }
            }
          }
        }
      }
    },
    [formValues, uploadFile, setValue]
  );

  // ── getServerErrorMessage ─────────────────────────────────────────────────

  const getServerErrorMessage = useCallback((err: any): string | null => {
    return getApiError(err, 'An error occurred') || null;
  }, []);

  // ── Email verification gate ───────────────────────────────────────────────

  const flattenFields = useCallback((fields: any[]): any[] =>
    (fields || []).flatMap((f: any) =>
      f.type === 'line' || f.type === 'container' ? flattenFields(f.nestedFields || []) : [f]
    ), []);

  // Returns the first visible, filled email field in the section that requires
  // verification and hasn't been verified for its current value — else null.
  const getUnverifiedEmailField = useCallback((section: any): any | null => {
    if (!sendEmailCode || !verifyEmailCode || !section) return null;
    for (const f of flattenFields(section.fields)) {
      if (f.type !== 'email' || !f.validateEmail) continue;
      if (!evaluateFieldVisibility(f.name, valuesWithReferral, undefined, f.conditionalLogic)) continue;
      const value = (formValues[f.name] ?? '').toString().trim();
      if (!value) continue; // empty optional field — nothing to verify
      if (emailVerifiedRef.current[f.name] === value) continue; // already verified
      return f;
    }
    return null;
  }, [sendEmailCode, verifyEmailCode, flattenFields, formValues, valuesWithReferral]);

  // ── handleNext ────────────────────────────────────────────────────────────

  const handleNext = useCallback(
    async (skipNavigation = false): Promise<boolean> => {
      if (!currentSection) return false;

      targetPageIndexRef.current = null;

      try {
        setSaving(true);

        // Step 1: Validate current section fields
        const fieldsToValidate = getFieldsToValidate(currentSection, valuesWithReferral);
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
          scrollToFirstError();
          return false;
        }

        // Step 1b: Email verification gate — if this section has an email field that
        // requires verification and isn't verified yet, send a code, open the popup,
        // and block navigation. handleNext re-runs after each successful verification.
        const emailFieldToVerify = getUnverifiedEmailField(currentSection);
        if (emailFieldToVerify) {
          const emailValue = (formValues[emailFieldToVerify.name] ?? '').toString().trim();
          const tok = await ensureToken();
          if (tok && tok !== token) {
            setToken(tok);
            onTokenAcquired?.(tok);
          }
          if (!tok) {
            setError('No token available. Please refresh and try again.');
            return false;
          }
          // Persist the section's data before starting the verification process so the
          // entered answers (including the email) aren't lost while verifying.
          try {
            await uploadPendingFiles(currentSection.fields, tok);
            const sectionAnswers = buildSectionAnswers(currentSection.fields, activeStep);
            await saveAnswers(tok, sectionAnswers, { title: currentSection.title, index: activeStep });
            setAllAnswers((prev) => ({
              ...prev,
              ...Object.fromEntries(sectionAnswers.map((a: any) => [a.question_name, a.answer])),
            }));
          } catch (err) {
            console.error('Error saving section before verification:', err);
            setError(getServerErrorMessage(err) || 'Failed to save your answers. Please try again.');
            return false;
          }
          try {
            await sendEmailCode!(tok, emailFieldToVerify.name, emailValue);
          } catch (err) {
            setError(getServerErrorMessage(err) || 'Failed to send the verification email.');
            return false;
          }
          setVerification({
            open: true,
            fieldName: emailFieldToVerify.name,
            email: emailValue,
            busy: false,
            error: null,
            info: null,
          });
          return false;
        }

        // Step 2: Upload pending files (if token already exists)
        if (token) {
          await uploadPendingFiles(currentSection.fields, token);
        }

        // Step 3: Ensure token
        const currentToken = await ensureToken();
        if (currentToken && currentToken !== token) {
          setToken(currentToken);
          onTokenAcquired?.(currentToken);
        }

        // Step 4: Upload pending files with new token (files selected before token was created)
        if (!token && currentToken) {
          await uploadPendingFiles(currentSection.fields, currentToken);
        }

        if (!currentToken) {
          setError('No token available. Please refresh and try again.');
          return false;
        }

        // Step 5: Build section answers
        const sectionAnswers = buildSectionAnswers(currentSection.fields, activeStep);

        // Step 6: Save answers
        await saveAnswers(currentToken, sectionAnswers, { title: currentSection.title, index: activeStep });

        // Step 7: Score detection — find button with next-and-score action
        const nextButton = currentSection.fields.find(
          (f: any) => f.type === 'button' && f.action === 'next-and-score' && f.scoreName
        );

        let updatedScores = { ...scores };

        if (nextButton && nextButton.scoreName) {
          try {
            const scoreResponse = await calculateScoreApi(currentToken, nextButton.scoreName);
            updatedScores = {
              ...updatedScores,
              [scoreResponse.score_name]: {
                score: scoreResponse.score,
                score_groups: scoreResponse.score_groups,
              },
            };
            updateScores(updatedScores);
          } catch (scoreErr) {
            console.error('Error calculating score:', scoreErr);
          }
        }

        // Step 8: Payment check
        const hasPaymentField = currentSection.fields.some((f: any) => f.type === 'stripe_cc');
        if (hasPaymentField) {
          if (!paymentFormReady) {
            setError('Please wait for the payment form to load completely.');
            return false;
          }
          if (stripeCCFieldRef.current) {
            const paymentResult = await stripeCCFieldRef.current.confirmPayment();
            if (!paymentResult.success) {
              return false;
            }
          }
        }

        // Step 9: Update allAnswers
        setAllAnswers((prev) => ({
          ...prev,
          ...Object.fromEntries(sectionAnswers.map((a: any) => [a.question_name, a.answer])),
        }));

        // Step 10: Navigate if not skipped
        if (!skipNavigation) {
          const sectionList = definition?.sections || [];
          let nextIndex = activeStep + 1;

          while (nextIndex < totalSections) {
            if (isSectionVisible(sectionList[nextIndex], updatedScores)) {
              await loadPaymentAmountIfNeeded(sectionList[nextIndex]);
              setError(null);
              setActiveStep(nextIndex);
              return true;
            }
            nextIndex++;
          }

          // No more visible sections — advance to review step
          if (nextIndex >= totalSections) {
            setError(null);
            setActiveStep(sections.length);
          }
        }

        return true;
      } catch (err) {
        console.error('Error saving section:', err);
        const serverMessage = getServerErrorMessage(err);
        setError(serverMessage || 'Failed to save your answers. Please try again.');
        if (serverMessage?.includes('already exists in our system')) {
          const idFieldSectionIdx = findSectionIndexByMapping(sections, 'id_number');
          if (idFieldSectionIdx >= 0) setActiveStep(idFieldSectionIdx);
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentSection,
      token,
      activeStep,
      scores,
      paymentFormReady,
      definition,
      sections,
      totalSections,
      buildSectionAnswers,
      uploadPendingFiles,
      ensureToken,
      saveAnswers,
      calculateScoreApi,
      updateScores,
      isSectionVisible,
      loadPaymentAmountIfNeeded,
      getUnverifiedEmailField,
      sendEmailCode,
      getServerErrorMessage,
      onTokenAcquired,
    ]
  );

  // ── Email verification popup handlers ─────────────────────────────────────

  const handleVerifyCode = useCallback(
    async (code: string) => {
      if (!token || !verification.fieldName || !verifyEmailCode) return;
      setVerification((v) => ({ ...v, busy: true, error: null, info: null }));
      try {
        const res = await verifyEmailCode(token, verification.fieldName, code);
        if (res?.verified) {
          // Mark verified synchronously (ref) so the re-run of handleNext proceeds
          emailVerifiedRef.current = {
            ...emailVerifiedRef.current,
            [verification.fieldName]: verification.email,
          };
          setVerification((v) => ({ ...v, open: false, busy: false, error: null }));
          handleNext();
        } else {
          setVerification((v) => ({
            ...v,
            busy: false,
            error: 'The code is invalid or has expired. Please try again.',
          }));
        }
      } catch (err) {
        setVerification((v) => ({
          ...v,
          busy: false,
          error: getServerErrorMessage(err) || 'The code is invalid or has expired. Please try again.',
        }));
      }
    },
    [token, verification.fieldName, verification.email, verifyEmailCode, handleNext, getServerErrorMessage]
  );

  const handleResendCode = useCallback(async () => {
    if (!token || !verification.fieldName || !sendEmailCode) return;
    setVerification((v) => ({ ...v, busy: true, error: null, info: null }));
    try {
      await sendEmailCode(token, verification.fieldName, verification.email);
      setVerification((v) => ({ ...v, busy: false, info: 'A new code was sent to your email.' }));
    } catch (err) {
      setVerification((v) => ({
        ...v,
        busy: false,
        error: getServerErrorMessage(err) || 'Failed to resend the verification email.',
      }));
    }
  }, [token, verification.fieldName, verification.email, sendEmailCode, getServerErrorMessage]);

  const handleCancelVerification = useCallback(() => {
    setVerification((v) => ({ ...v, open: false, busy: false, error: null, info: null }));
  }, []);

  // ── handleSaveCurrentPage ─────────────────────────────────────────────────

  const handleSaveCurrentPage = useCallback(async (): Promise<boolean> => {
    if (!currentSection) return false;

    try {
      setSaving(true);

      // Step 1: Validate current page fields (excluding ones hidden by conditional logic)
      const pageFields = getFieldsForCurrentPage();
      const fieldNames = pageFields
        .filter((f: any) => f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner')
        .filter((f: any) => evaluateFieldVisibility(f.name, valuesWithReferral, undefined, f.conditionalLogic))
        .map((f: any) => f.name);

      const isValid = await trigger(fieldNames);
      if (!isValid) {
        scrollToFirstError();
        return false;
      }

      // Step 2: Get current token
      const currentToken = token;
      if (!currentToken) {
        setError('No token available. Please refresh and try again.');
        return false;
      }

      // Step 3: Upload pending files for current page
      await uploadPendingFiles(pageFields.filter((f: any) => f.type === 'file'), currentToken);

      // Step 4: Build answers for current page only
      const pageAnswers = pageFields
        .filter((f: any) => f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner')
        .map((field: any, index: number) => {
          const value = formValues[field.name];
          return {
            order: activeStep * 100 + sectionPageIndex * 10 + index + 1,
            question_name: field.name,
            answer: convertToAnswer(value, field.type),
          };
        });

      // Step 5: Save
      await saveAnswers(currentToken, pageAnswers);

      // Step 6: Navigate to next page
      setSectionPageIndex((prev) => prev + 1);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      autoAdvanceTriggeredRef.current = false;
      setCameFromBackNav(false);
      return true;
    } catch (err) {
      console.error('Error saving page:', err);
      setError('Failed to save answers. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    currentSection,
    token,
    activeStep,
    sectionPageIndex,
    formValues,
    valuesWithReferral,
    getFieldsForCurrentPage,
    uploadPendingFiles,
    saveAnswers,
    trigger,
    scrollToFirstError,
    setSaving,
    setSectionPageIndex,
    scrollContainerRef,
    autoAdvanceTriggeredRef,
    setCameFromBackNav,
  ]);

  // ── handleSubmit ──────────────────────────────────────────────────────────

  const handleSubmitQuestionnaire = useCallback(async (): Promise<void> => {
    setSaving(true);
    try {
      const isReviewStep = activeStep === sections.length;

      // Track active token — may be created fresh on first submit
      let activeToken = token;

      // If on a real section (not review step): validate, upload, build answers, save
      if (!isReviewStep && currentSection) {
        const fieldsToValidate = getFieldsToValidate(currentSection, valuesWithReferral);
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
          scrollToFirstError();
          return;
        }

        // Ensure token exists (creates one on first submit if none yet)
        const currentToken = await ensureToken();
        if (currentToken && currentToken !== token) {
          setToken(currentToken);
          onTokenAcquired?.(currentToken);
        }

        if (!currentToken) {
          setError('No token available. Please refresh and try again.');
          return;
        }

        activeToken = currentToken;

        await uploadPendingFiles(currentSection.fields, activeToken);

        // On final submit, persist ALL sections' answers (built from the current form
        // values) so nothing entered on earlier/current sections is missed — e.g. the
        // embedded collect_info flow (DIVA) where only the last section triggers submit.
        // (Safe because these flows have no conditional-visibility/payment sections.)
        const sectionAnswers = isEmbedded
          ? (definition?.sections || []).flatMap((s: any, idx: number) => buildSectionAnswers(s.fields, idx))
          : buildSectionAnswers(currentSection.fields, activeStep);
        // Pass the section metadata (title/index) like handleNext does, so the backend
        // records last_section and runs apply_section_patient_field_update for the final
        // section — otherwise the last section's answers aren't fully applied on submit.
        await saveAnswers(activeToken, sectionAnswers, { title: currentSection.title, index: activeStep });

        // Payment check
        const hasPaymentField = currentSection.fields.some((f: any) => f.type === 'stripe_cc');
        if (hasPaymentField) {
          if (!paymentFormReady) {
            setError('Please wait for the payment form to load completely.');
            return;
          }
          if (stripeCCFieldRef.current) {
            const paymentResult = await stripeCCFieldRef.current.confirmPayment();
            if (!paymentResult.success) {
              return;
            }
          }
        }

      }

      if (!activeToken) {
        setError('No token available. Please refresh and try again.');
        return;
      }

      const submitResult = await submitQuestionnaire(activeToken);

      // The backend may return a redirect_url (e.g. il_webhook meeting_address →
      // the meeting's team_url) — send the patient straight there.
      if (submitResult?.redirect_url) {
        skipBeforeUnloadRef.current = true;
        window.location.href = submitResult.redirect_url;
        return;
      }

      // If more visible sections remain, navigate to the next one; otherwise complete
      const sectionList = definition?.sections || [];
      let nextIndex = activeStep + 1;
      while (nextIndex < totalSections) {
        if (isSectionVisible(sectionList[nextIndex], scores)) {
          await loadPaymentAmountIfNeeded(sectionList[nextIndex]);
          setSubmittedAtIndex(activeStep);
          setError(null);
          setActiveStep(nextIndex);
          return;
        }
        nextIndex++;
      }

      onComplete();
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
      const serverMessage = getServerErrorMessage(err);
      if (serverMessage) {
        setError(serverMessage);
        if (serverMessage.includes('already exists in our system')) {
          const idFieldSectionIdx = findSectionIndexByMapping(sections, 'id_number');
          if (idFieldSectionIdx >= 0) setActiveStep(idFieldSectionIdx);
        }
      } else {
        toast.error('Failed to complete questionnaire');
      }
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStep,
    sections,
    currentSection,
    token,
    isEmbedded,
    paymentFormReady,
    definition,
    totalSections,
    scores,
    isSectionVisible,
    loadPaymentAmountIfNeeded,
    buildSectionAnswers,
    uploadPendingFiles,
    ensureToken,
    onTokenAcquired,
    saveAnswers,
    submitQuestionnaire,
    onComplete,
    trigger,
    scrollToFirstError,
  ]);

  // ── handleExit ────────────────────────────────────────────────────────────

  const handleExit = useCallback(
    async (exitUrl?: string): Promise<void> => {
      setSaving(true);
      try {
        await onExitConfirmed(token);
      } catch (err) {
        console.error('Error exiting questionnaire:', err);
      } finally {
        skipBeforeUnloadRef.current = true;
        onExitNavigate(exitUrl);
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, onExitConfirmed, onExitNavigate, skipBeforeUnloadRef]
  );

  // ── handleConfirmedExit ───────────────────────────────────────────────────

  const handleConfirmedExit = useCallback(async (): Promise<void> => {
    const exitPopupTitle = definition?.popups?.find((p: any) => p.id === exitPopupId)?.title;
    setSaving(true);
    try {
      await onExitConfirmed(token, exitPopupTitle);
      setShowExitPopup(false);
      skipBeforeUnloadRef.current = true;
      onExitNavigate();
    } catch (err) {
      console.error('Error exiting questionnaire:', err);
      setShowExitPopup(false);
      skipBeforeUnloadRef.current = true;
      onExitNavigate();
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, onExitConfirmed, onExitNavigate, setShowExitPopup, skipBeforeUnloadRef, definition, exitPopupId]);

  // ── handlePopupButtonClick ────────────────────────────────────────────────

  const handlePopupButtonClick = useCallback(
    (button: PopupButton) => {
      switch (button.action) {
        case 'close-popup':
          handleClosePopup();
          if (navigateAfterPopup) {
            setNavigateAfterPopup(false);
            if (activeStep < totalSections - 1) {
              const sectionList = definition?.sections || [];
              let nextIndex = activeStep + 1;
              while (nextIndex < totalSections) {
                if (isSectionVisible(sectionList[nextIndex], scores)) {
                  loadPaymentAmountIfNeeded(sectionList[nextIndex]);
                  setActiveStep(nextIndex);
                  break;
                }
                nextIndex++;
              }
            }
          } else if (navigateToPage !== null) {
            setSectionPageIndex(navigateToPage);
            setNavigateToPage(null);
            autoAdvanceTriggeredRef.current = false;
          }
          break;
        case 'next':
          handleClosePopup();
          if (navigateAfterPopup) {
            setNavigateAfterPopup(false);
            if (activeStep < totalSections - 1) {
              const sectionList = definition?.sections || [];
              let nextIndex = activeStep + 1;
              while (nextIndex < totalSections) {
                if (isSectionVisible(sectionList[nextIndex], scores)) {
                  loadPaymentAmountIfNeeded(sectionList[nextIndex]);
                  setActiveStep(nextIndex);
                  break;
                }
                nextIndex++;
              }
            }
          } else if (navigateToPage !== null) {
            setSectionPageIndex(navigateToPage);
            setNavigateToPage(null);
            autoAdvanceTriggeredRef.current = false;
          } else {
            handleNext();
          }
          break;
        case 'exit-screening': {
          const exitPopupTitle = definition?.popups?.find((p: any) => p.id === openPopupId)?.title;
          handleClosePopup();
          (async () => {
            try {
              setSaving(true);
              await onExitConfirmed(token, exitPopupTitle);
              skipBeforeUnloadRef.current = true;
              onExitNavigate();
            } catch (err) {
              console.error('Error exiting questionnaire:', err);
              skipBeforeUnloadRef.current = true;
              onExitNavigate();
            } finally {
              setSaving(false);
            }
          })();
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleClosePopup,
      navigateAfterPopup,
      navigateToPage,
      activeStep,
      totalSections,
      definition,
      scores,
      isSectionVisible,
      loadPaymentAmountIfNeeded,
      setNavigateAfterPopup,
      setSectionPageIndex,
      setNavigateToPage,
      autoAdvanceTriggeredRef,
      handleNext,
      token,
      onExitConfirmed,
      onExitNavigate,
      skipBeforeUnloadRef,
      openPopupId,
    ]
  );

  // ── buildWizardFieldProps ─────────────────────────────────────────────────

  const isReviewStep = activeStep === sections.length;

  const buildWizardFieldProps = () => ({
    control,
    errors,
    // Field-level visibility (WizardRenderField) needs the synthetic __referral__
    // and __lead__ keys, which live on valuesWithReferral, not the raw form values.
    formValues: valuesWithReferral,
    scores,
    variableValues,
    isMobile,
    saving,
    token,
    questionnaireId,
    paymentAmount,
    paymentConfigAmount: configuredPaymentAmount,
    paymentDiscountText,
    paymentButtonLabel: definition?.payment_config?.buttonLabel,
    stripeCCFieldRef,
    onOpenPopup: handleOpenPopup,
    onSubmit: handleSubmitQuestionnaire,
    onNext: async (opts?: { skipNavigation?: boolean; shouldCalculateScore?: boolean; scoreName?: string }) =>
      handleNext(opts?.skipNavigation || false),
    onExit: (exitUrl?: string) => {
      handleExit(exitUrl);
    },
    onTriggerExitPopup: triggerExitPopup,
    onSaveCurrentPage: handleSaveCurrentPage,
    onMobileNextPage: async () => {
      const pageFields = getFieldsForCurrentPage();
      const fieldNames = pageFields
        .filter((f: any) => f.type !== 'button')
        .filter((f: any) => evaluateFieldVisibility(f.name, valuesWithReferral, undefined, f.conditionalLogic))
        .map((f: any) => f.name);
      const isValid = await trigger(fieldNames);
      if (isValid) {
        setSectionPageIndex((prev) => prev + 1);
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    hasMorePages,
    shouldValidateBeforePopup: true,
    onTriggerValidation: (fieldNames: string[]) => trigger(fieldNames),
    onScrollToTop: () => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' }),
    onScrollToFirstError: scrollToFirstError,
    currentSectionFields: currentSection?.fields,
    onPaymentReady: setPaymentFormReady,
    onPaymentSuccess: () => {
      // Payment succeeded → the flow is complete and everything is saved, so the
      // final "What happens next?" screen must not trigger the leave-site warning.
      skipBeforeUnloadRef.current = true;
      if (activeStep < totalSections - 1) {
        const sectionList = definition?.sections || [];
        let nextIndex = activeStep + 1;
        while (nextIndex < totalSections) {
          if (isSectionVisible(sectionList[nextIndex], scores)) {
            setActiveStep(nextIndex);
            break;
          }
          nextIndex++;
        }
      }
    },
    onSetButtonGroupPopupContext: setButtonGroupPopupContext,
    onSetNavigateAfterPopup: setNavigateAfterPopup,
    injectCustomIcons,
    language,
    clinicState,
  });

  // ── Review step content ───────────────────────────────────────────────────

  const reviewBodyContent = (
    <Box sx={{ mt: 0 }}>
      {definition.sections.map((section: any) => (
        <Box
          key={section.id}
          sx={{
            mb: 3,
            p: 2,
            border: '1px solid #E5F0F1',
            borderRadius: '12px',
            backgroundColor: '#F9FAFB',
          }}
        >
          <Typography
            sx={{
              color: '#3A7380',
              fontWeight: 600,
              fontSize: '1.1rem',
              fontFamily: '"Open Sans", sans-serif',
              mb: 2,
            }}
          >
            {section.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {section.fields.map((field: any) => {
              const value = allAnswers[field.name];
              if (!value || (Array.isArray(value) && value.length === 0)) return null;
              return (
                <Box key={field.name}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#5A7380',
                      fontFamily: '"Open Sans", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#3A7380', fontFamily: '"Open Sans", sans-serif' }}
                  >
                    {Array.isArray(value) ? value.join(', ') : value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const reviewFooterContent = (
    <>
      {activeStep > 0 && (
        <Button
          onClick={navigateToPrevVisibleSection}
          disabled={saving}
          sx={{
            borderColor: '#B0B0B0',
            color: '#616161',
            backgroundColor: 'transparent',
            borderRadius: '10px',
            minWidth: '100px',
            height: '40px',
            paddingX: '16px',
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            fontFamily: '"Open Sans", sans-serif',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          Back
        </Button>
      )}
      <Button
        onClick={handleSubmitQuestionnaire}
        variant="contained"
        disabled={saving}
        sx={{
          backgroundColor: '#3A7380',
          color: 'white',
          borderRadius: '10px',
          minWidth: '120px',
          height: '40px',
          paddingX: '20px',
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '0.05em',
          boxShadow: 'none',
          fontFamily: '"Open Sans", sans-serif',
          '&:hover': { backgroundColor: '#2e5c66', boxShadow: 'none' },
          '&:disabled': { backgroundColor: '#B0B0B0' },
        }}
      >
        {saving ? 'Submitting...' : 'Submit'}
      </Button>
    </>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const fieldProps = buildWizardFieldProps();
  const renderField = (field: any) => (
    <WizardRenderField
      field={field}
      fieldKey={`section-${activeStep}-${field.name}`}
      {...fieldProps}
    />
  );

  return (
    <Box dir={language === 'he' ? 'rtl' : 'ltr'} sx={{ height: isEmbedded ? '100%' : undefined, minHeight: isEmbedded ? undefined : '100%' }}>
      <WizardLayout
        isMobile={isMobile}
        sectionPageIndex={sectionPageIndex}
        sectionIndex={activeStep}
        currentSection={currentSection}
        scrollContainerRef={scrollContainerRef}
        navigateToPrevVisibleSection={navigateToPrevVisibleSection}
        sections={sections}
        scores={scores}
        isReviewStep={isReviewStep}
        minSectionIndex={submittedAtIndex}
        isRTL={language === 'he'}
        isEmbedded={isEmbedded}
        logoSrc={logoSrc}
        rightLogoSrc={rightLogoSrc}
      >
        <WizardSectionContent
          currentSection={currentSection}
          sectionIndex={activeStep}
          isMobile={isMobile}
          sectionPageIndex={sectionPageIndex}
          formValues={valuesWithReferral}
          cameFromBackNav={cameFromBackNav}
          hasMorePages={hasMorePages}
          getFieldsForCurrentPage={getFieldsForCurrentPage}
          renderField={renderField}
          error={error}
          isReviewStep={isReviewStep}
          reviewBodyContent={reviewBodyContent}
          reviewFooterContent={reviewFooterContent}
          useActualButtonsInDefault={true}
        />
      </WizardLayout>
      <WizardPopupSystem
        openPopupId={openPopupId}
        popups={definition?.popups}
        onClosePopup={handleClosePopup}
        onPopupButtonClick={handlePopupButtonClick}
        saving={saving}
        isMobile={isMobile}
        showExitPopup={showExitPopup}
        exitPopupId={exitPopupId}
        onCloseExitPopup={() => setShowExitPopup(false)}
        onConfirmedExit={handleConfirmedExit}
        exitPopupContent={getExitPopupContent()}
      />
      <EmailVerificationPopup
        open={verification.open}
        email={verification.email}
        busy={verification.busy}
        error={verification.error}
        info={verification.info}
        isRTL={language === 'he'}
        onCancel={handleCancelVerification}
        onResend={handleResendCode}
        onValidate={handleVerifyCode}
      />
    </Box>
  );
};
